import { useState, useEffect, useContext } from "react";
import moment from 'moment-timezone';
import useMediaQuery from '@mui/material/useMediaQuery';
import { ResponsiveChartContainer } from '@mui/x-charts/ResponsiveChartContainer';
import { BarPlot } from '@mui/x-charts/BarChart';
import { ChartsGrid } from '@mui/x-charts/ChartsGrid';
import { ChartsXAxis } from '@mui/x-charts/ChartsXAxis';
import { ChartsYAxis } from '@mui/x-charts/ChartsYAxis';
import { ChartsTooltip } from "@mui/x-charts/ChartsTooltip";
import { ChartsAxisHighlight } from '@mui/x-charts/ChartsAxisHighlight';
import { ChartsReferenceLine } from '@mui/x-charts/ChartsReferenceLine'
import { Box, Grid, Typography, Stack } from '@mui/material'
import ChartTotals from "./chartTotals";
import ChargeLevelChart from "../right-panel/monitor/chargeLevelChart";
import { DataContext } from '../../contexts/data/dataContext'
import custom from '../../styles/mui/szVariables.json'


export default function Charts({ id, stackChart }: any) {
    const smaller = useMediaQuery('(max-width:850px)');
    const data = useContext(DataContext)
    const [series, setSeries] = useState([] as any[]);
    const [skip, setSkip] = useState(true);
    const selectedData = data?.reports?.selectedData || {}
    const tou = selectedData?.tou;
    const labels = data?.reports?.labels;
    const averageLine = !selectedData?.referenceLine && 
                            id!=='grid' && id!=='battery'
    let hours = []
    if(tou?.isWeekend===true) {
        hours = (data?.customer?.provider?.details?.weekends?.hours || [])
                .sort((a:any,b:any)=>a.idx-b.idx)
    } else if(tou?.isWeekend===false){
        hours = (data?.customer?.provider?.details?.weekdays?.hours || [])
                .sort((a:any,b:any)=>a.idx-b.idx)
    }
    hours = hours.map((h:any)=>{
        if(h.type==='offPeak') h.label = "Off-peak"
        if(h.type==='shoulder') h.label = "Shoulder"
        if(h.type==='peak') h.label = "Peak"
        return h
    })

    const getDayOfWeek = (day: any) => {
        if(selectedData?.tip !=='am' && selectedData?.startDate) {
            const startDate = moment.tz(
                moment(selectedData?.startDate),
                selectedData?.timezone
            )
            return `${startDate.date(day).format('dddd')}, `
        }
        return ''
    }
    
    const valueFormatter =  (val:number)=>`${Math.abs(val)} kWh`
    useEffect(() => {
        if(data?.reports?.selectedData) {
            const selectedStack = data?.reports?.selectedData[id]?.stack || []
            const selectedSeries = data?.reports?.selectedData[id]?.series 
            const reportColor = data?.reports?.colors[id]
            if(stackChart) {
                setSeries((selectedStack)
                    .map((s:any)=>{
                        return { type:'bar', 
                                 data: s.values, 
                                 label: labels[id+s.id], 
                                 id: s.id, 
                                 color: s.color , 
                                 stack: 'total',
                                 valueFormatter,
                            }
                    }))
            } else {
                if(data?.reports?.selectedData[id]?.stackSeries) {
                    setSeries((selectedSeries)
                    .map((s:any)=>{
                        return { type:'bar', 
                                 data: s.values, 
                                 label: labels[s.id], 
                                 id: s.id, 
                                 color: s.color , 
                                 stack: 'total',
                                 valueFormatter
                              }
                    }))
                } else {
                    setSeries([ 
                        { type:'bar', 
                          data: selectedSeries, 
                          label: labels[id], 
                          id, 
                          color: reportColor,
                          valueFormatter,
                        } 
                    ])
                }
                
            }
        }
        setSkip(false)
      },[
        id,
        labels, 
        data?.reports?.selectedData, 
        data?.reports?.colors,
        stackChart
    ])

    if(!data?.reports?.selectedData) return null;
      
    const phone = (h: any) => 
        (h.type==='shoulder' && 
        smaller &&
        tou?.isWeekend===false)

    return (
        <Grid container direction="column" 
                justifyContent="flex-start" 
                id={id+'ChartContent'}
                px={custom.spacing.sm}
                pt={custom.spacing.sm}>
            <Grid item maxWidth={"100%"} >
                <ChartTotals id={id}
                    content={selectedData[id]}
                    tou={!!tou} />
            </Grid>
            <Grid item maxWidth={"100%"} 
                    justifyContent="center" 
                    alignItems="flex-start" 
                    alignContent={"flex-start"}
                    display="flex"
                    flexDirection={"column"}>
                <Box width = {{ 
                        xs:'80vw', 
                        sm: '400px',
                        md: data.rightPanel?'450px':'500px',
                        lg: data.rightPanel?'650px':'883px'
                    }} 
                    id={id+'BarChart'} 
                    display="flex">
                    <ResponsiveChartContainer
                            series={series}
                            xAxis={[{ 
                                data: [...selectedData?.xAxesLabels], 
                                scaleType: 'band', 
                                id: 'x-axis-id',
                                valueFormatter: (val, context) =>
                                    context.location === 'tick'
                                      ? val
                                      : val.endsWith('m')
                                        ? val
                                        : val.endsWith(' ')
                                            ? val.replace(' ','pm')
                                            : `${getDayOfWeek(val)}${val}${selectedData?.tip}`
                            }]}
                            height={300}
                            margin={{
                                left: 10,
                                right: 60,
                                top: 50,
                                bottom: 20,
                            }} >
                        <BarPlot skipAnimation={skip} />
                        <ChartsGrid horizontal={true}  />
                        <ChartsXAxis position="bottom" axisId="x-axis-id" 
                            disableTicks={true} tickLabelPlacement='middle'
                            
                            />
                        <ChartsYAxis position="right" 
                                disableTicks={true} 
                                tickLabelPlacement='tick' 
                                valueFormatter={
                                    data?.reports?.selectedData[id]?.formatter
                                }
                                tickLabelStyle={{ fontSize: '10px' }}
                            />
                        
                        {averageLine && 
                            <ChartsReferenceLine y={`${selectedData[id]?.average || ''}`} 
                                label={`avg`} 
                                labelAlign="end"
                                // spacing={{x:-30, y:-3}}
                                labelStyle={{
                                    fontFamily:'GT Pressura Regular', 
                                    fontSize: '12px',
                                    // fill: '#0F0',
                                }} 
                                lineStyle={{ 
                                    // strokeDasharray: "10 10", 
                                    strokeWidth: "2", 
                                    strokeOpacity: 1
                                }} />   
                        }
                        {hours.map((h: any, i: number)=>(
                            <ChartsReferenceLine 
                                key={`refLine${i}`}
                                label={h.label} 
                                spacing={{
                                    x:phone(h)?5:0,
                                    y:phone(h)?-25:-15}} 
                                labelStyle={{ 
                                        fontFamily:'GT Pressura Regular', 
                                        fontSize: '10px', 
                                        color: '#D8D8DA',
                                    }} 
                                labelAlign="start"  
                                x={
                                    h.from===0?'12am':h.from>12?`${h.from-12} `:`${h.from}`
                                }  
                                lineStyle={{ 
                                        strokeDasharray: "10 5", 
                                        strokeWidth: "1", 
                                        strokeOpacity: .3
                                    }} 
                            />
                        ))}
                        {(selectedData[id]?.mondays || [])
                            .map((m: any, i: number)=>(
                            <ChartsReferenceLine 
                                key={`refLine${i}`}
                                x={m}  
                                lineStyle={{ 
                                        strokeDasharray: "10 5", 
                                        strokeWidth: "1", 
                                        strokeOpacity: 1
                                    }} 
                            />
                        ))}
                        <ChartsTooltip />
                        <ChartsAxisHighlight x="line" y="none" />
                    </ResponsiveChartContainer>
                </Box>
                <Box display="flex" flexDirection={"column"}
                    width = {{ 
                        xs:'80vw', 
                        sm: '400px',
                        md: data.rightPanel?'450px':'500px',
                        lg: data.rightPanel?'650px':'883px' 
                    }}>
                    {id==='battery' && selectedData?.batteryStateOfCharge &&
                    <>
                        <Typography color="text.primary" 
                                    mt={custom.spacing.md}
                                    ml={custom.spacing.xs}
                                    variant="szHeading.Regular.Small"
                                    id={id+'ChartTitle'}>
                            Battery charge level        
                        </Typography>
                        <ChargeLevelChart id='performanceBattery'
                                content={selectedData?.batteryStateOfCharge} 
                                margin={{
                                    left: 10,
                                    right: 60,
                                    top: 20,
                                    bottom: 20,
                                }} />
                    </>
                    }
                        
                </Box>
                {averageLine && 
                    <Stack mt={custom.spacing.md} 
                        width="100%"
                        alignContent={"center"}
                        direction={"row"}
                        justifyContent={"center"}>
                        <Typography variant="szHeading.Regular.xSmall">
                            Average
                        </Typography>
                        <Typography ml="auto" 
                             id="dailyAverage"
                             variant="szBody.Medium.Small">
                            {selectedData[id]?.average} kWh / day
                        </Typography>
                    </Stack>
                }
            </Grid>
        </Grid>
        
    )
}